import { Button } from '@insify/design-system/components';
import { ArrowLeft, ArrowRight } from '@insify/design-system/icons';
import clsx from 'clsx';
import { useRef, useState } from 'react';

import * as styles from './Slider.module.css';

import type React from 'react';

type SliderProps = {
  slides: React.ReactElement[];
  containerPadding?: number;
  containerMaxWidth?: number;
  slideContentWidth: number;
  slideGap?: number;
  className?: string;
  alignStepper?: 'left' | 'right' | 'center';
};

export const Slider: React.FC<SliderProps> = ({
  slides,
  containerPadding = 16,
  containerMaxWidth = 480,
  slideContentWidth,
  slideGap = 16,
  alignStepper = 'center',
  className,
}): JSX.Element => {
  const [scrollState, setScrollState] = useState<'start' | 'scrolling' | 'end'>('start');
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const containerWidth = containerRef.current?.clientWidth ?? 0;
  const padding = containerWidth > containerMaxWidth ? (containerWidth - containerMaxWidth) / 2 : containerPadding;
  const slideWidth = slideContentWidth + slideGap;

  const getScrollDistances = () => {
    const currentSlideOffset = (containerRef.current?.scrollLeft ?? 0) % slideWidth;
    const hasScrolledPastCenterOfSlide = currentSlideOffset > slideWidth / 2;
    const distanceCurrentSlideForward = slideWidth - currentSlideOffset;
    const distanceCurrentSlideBackward = -currentSlideOffset;
    const distanceNextSlide = slideWidth * 2 - currentSlideOffset;
    const distancePreviousSlide = (slideWidth + currentSlideOffset) * -1;

    return {
      hasScrolledPastCenterOfSlide,
      distanceCurrentSlideForward,
      distanceCurrentSlideBackward,
      distanceNextSlide,
      distancePreviousSlide,
    };
  };

  const forward: React.MouseEventHandler = (): void => {
    const { hasScrolledPastCenterOfSlide, distanceCurrentSlideForward, distanceNextSlide } = getScrollDistances();
    containerRef.current?.scrollBy({
      left: hasScrolledPastCenterOfSlide ? distanceNextSlide : distanceCurrentSlideForward,
      behavior: 'smooth',
    });
  };

  const backward: React.MouseEventHandler = (): void => {
    const { hasScrolledPastCenterOfSlide, distanceCurrentSlideBackward, distancePreviousSlide } = getScrollDistances();
    containerRef.current?.scrollBy({
      left: hasScrolledPastCenterOfSlide ? distanceCurrentSlideBackward : distancePreviousSlide,
      behavior: 'smooth',
    });
  };

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (event): void => {
    const { scrollLeft, scrollWidth, clientWidth } = event.currentTarget;
    if (scrollLeft === 0) {
      return setScrollState('start');
    }
    if (scrollLeft + clientWidth === scrollWidth) {
      return setScrollState('end');
    }
    if (scrollState !== 'scrolling') {
      setScrollState('scrolling');
    }
  };

  return (
    <div className={clsx(styles.wrapper, className)}>
      <div ref={containerRef} className={styles.container} onScroll={handleScroll}>
        <div
          className={styles.content}
          ref={contentRef}
          style={{ columnGap: `${slideGap}px`, paddingLeft: `${padding}px`, paddingRight: `${padding}px` }}
        >
          {slides.map((child) => (
            <div
              key={child.key}
              style={{
                width: `${slideContentWidth}px`,
                display: 'flex',
                justifyContent: 'stretch',
                overflowX: 'hidden',
                height: 'fit-content',
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
      <div
        style={{
          paddingLeft: `${padding}px`,
          paddingRight: `${padding}px`,
        }}
        className={clsx(styles.stepper, styles[`${alignStepper}AlignStepper`])}
      >
        <Button
          trackingId='previuos-claim-example'
          kind='secondary'
          type='button'
          className={styles.button}
          disabled={scrollState === 'start'}
          onClick={backward}
          leftIcon={<ArrowLeft className={styles.icon} />}
        />
        <Button
          trackingId='next-claim-example'
          kind='secondary'
          type='button'
          className={styles.button}
          disabled={scrollState === 'end'}
          onClick={forward}
          leftIcon={<ArrowRight className={styles.icon} />}
        />
      </div>
    </div>
  );
};
